/**
 * The learner's history (D-174), shared by the web and the phone: how to read a row of
 * `academy/history/`, where it leads, and the evolution of each skill between sittings of the same
 * kind, read from `academy/progress/` (`evolution`): never a second calculation.
 */

export const HISTORY_TYPES = ["mock", "practice", "placement", "discovery", "course", "coached", "challenge"] as const;
export type HistoryType = (typeof HISTORY_TYPES)[number];

export const HISTORY_SKILLS = ["co", "ce", "ee", "eo"] as const;
export type HistorySkill = (typeof HISTORY_SKILLS)[number];

export type HistoryScoreKind = "band" | "percent" | "nclc" | "score";

export interface HistoryScore {
  skill: string;
  kind: HistoryScoreKind;
  value: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function number(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** The figure that speaks for a result: IELTS band, German percentage, NCLC, then the raw score. */
export function resultFigure(row: unknown): { kind: HistoryScoreKind; value: number } | null {
  if (!isObject(row)) return null;
  for (const kind of ["band", "percent", "nclc", "score"] as const) {
    const value = number(row[kind]);
    if (value !== null) return { kind, value };
  }
  return null;
}

/** One figure per skill of a sitting, in the order of the exam. */
export function historyScores(scores: unknown): HistoryScore[] {
  if (!isObject(scores)) return [];
  return HISTORY_SKILLS.flatMap((skill) => {
    const figure = resultFigure(scores[skill]);
    return figure ? [{ skill, ...figure }] : [];
  });
}

/** The title of a row: a lesson's is `{fr, en}` in the language of the interface. */
export function historyTitle(title: unknown, language: string): string {
  if (typeof title === "string") return title;
  if (!isObject(title)) return "";
  const wanted = title[language.slice(0, 2)];
  if (typeof wanted === "string" && wanted) return wanted;
  const other = Object.values(title).find((v) => typeof v === "string" && v);
  return typeof other === "string" ? other : "";
}

/** Whole minutes, at least 1, or null when the duration is not known. */
export function historyMinutes(seconds: number | null | undefined): number | null {
  if (typeof seconds !== "number" || seconds < 0) return null;
  return Math.max(Math.round(seconds / 60), 1);
}

export type HistoryDestination =
  | { kind: "player"; id: string }
  | { kind: "correction"; id: string }
  | { kind: "lessonPlayer"; id: string }
  | { kind: "lessonCorrection"; id: string }
  | { kind: "challengeCorrection"; id: string };

interface DestinationRow {
  id: string;
  source: string;
  status: string;
  lesson: string | null;
}

/** An open sitting opens the player, never a correction (the answers stay secret). */
export function historyDestination(row: DestinationRow): HistoryDestination {
  if (row.source === "challenge") return { kind: "challengeCorrection", id: row.id };
  if (row.source === "lesson" || row.source === "lesson_try") {
    return row.status === "in_progress" && row.lesson ? { kind: "lessonPlayer", id: row.lesson } : { kind: "lessonCorrection", id: row.id };
  }
  return row.status === "in_progress" ? { kind: "player", id: row.id } : { kind: "correction", id: row.id };
}

export interface TrendPoint {
  at: string;
  attempt: string;
  value: number;
}

export interface SkillTrend {
  skill: HistorySkill;
  kind: HistoryScoreKind;
  points: TrendPoint[];
  first: number;
  last: number;
  delta: number;
  direction: "up" | "down" | "same";
}

function evolutionRows(evolution: unknown): Record<string, unknown>[] {
  return Array.isArray(evolution) ? evolution.filter(isObject) : [];
}

/** The kinds of sitting present in the evolution, most used first. */
export function evolutionKinds(evolution: unknown): string[] {
  const counts = new Map<string, number>();
  for (const row of evolutionRows(evolution)) {
    if (typeof row.kind === "string") counts.set(row.kind, (counts.get(row.kind) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([kind]) => kind);
}

/** The score of each skill along the graded sittings of one kind (German: `modules`). Skills are
 * kept apart: a figure is only compared with the same figure of the same skill. */
export function skillTrends(evolution: unknown, kind: string): SkillTrend[] {
  const rows = evolutionRows(evolution).filter((row) => row.kind === kind);
  const out: SkillTrend[] = [];
  for (const skill of HISTORY_SKILLS) {
    const points: (TrendPoint & { kind: HistoryScoreKind })[] = [];
    for (const row of rows) {
      const cells = isObject(row.skills) ? row.skills : isObject(row.modules) ? row.modules : {};
      const cell = cells[skill];
      const value = isObject(cell) ? (number(cell.band) ?? number(cell.percent) ?? number(cell.score)) : null;
      if (value === null || !isObject(cell)) continue;
      const figure: HistoryScoreKind = number(cell.band) !== null ? "band" : number(cell.percent) !== null ? "percent" : "score";
      points.push({ at: String(row.at ?? ""), attempt: String(row.attempt ?? ""), value, kind: figure });
    }
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last) continue;
    const delta = Math.round((last.value - first.value) * 10) / 10;
    out.push({
      skill,
      kind: last.kind,
      points: points.map(({ at, attempt, value }) => ({ at, attempt, value })),
      first: first.value,
      last: last.value,
      delta,
      direction: delta > 0 ? "up" : delta < 0 ? "down" : "same",
    });
  }
  return out;
}

export interface LessonProductionView {
  id: string;
  text: string;
  wordCount: number;
  status: string;
  grade: unknown;
  band: number | null;
  score: number | null;
  audioUrl: string | null;
  audioExpired: boolean;
}

/** The « write » or « speak » answer of an exercise in a lesson's saved correction. */
export function parseLessonProduction(value: unknown): LessonProductionView | null {
  if (!isObject(value)) return null;
  return {
    id: String(value.id ?? ""),
    text: typeof value.text === "string" ? value.text : "",
    wordCount: number(value.word_count) ?? 0,
    status: String(value.status ?? ""),
    grade: value.grade,
    band: number(value.band),
    score: number(value.score),
    audioUrl: typeof value.audio_url === "string" && value.audio_url ? value.audio_url : null,
    audioExpired: value.audio_expired === true,
  };
}

/** Whether the review says this recording is no longer kept (D-174). */
export function recordingExpired(row: unknown): boolean {
  return isObject(row) && row.audio_expired === true;
}
